import { useCallback, useRef, useState } from "react";

import { MoreSheet } from "@/components/shell/MoreSheet";
import { NavItem } from "@/components/shell/NavItem";
import { MenuIcon } from "@/components/ui/icons";
import { PHONE_BAR_DESTINATIONS } from "@/lib/destinations";

/**
 * DESIGN.md § Components `app-shell`, phone half: a 60px bottom bar with
 * five slots — Home · Properties · Tenants & Leases · Equipment · Menu
 * (VC-2.1-08, relabeled "More" → "Menu" by Story 20.1). "Menu" opens
 * `MoreSheet`; it never carries the active-accent
 * styling itself, even when the current route is one of the five behind it
 * — it is a plain button, not a `NavLink`, so it has no URL to match against
 * in the first place.
 */
export function PhoneBottomBar() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  // Stable across renders: MoreSheet's focus-restore effect depends on this
  // reference, and a fresh inline arrow function every render would tear
  // down and rebuild that effect on any re-render of this subtree while the
  // sheet is open, yanking focus back to the trigger and re-focusing the
  // first item. Not reachable today, but not worth leaving as a landmine.
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  return (
    <>
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 flex h-bottom-bar items-stretch border-t border-border-hairline bg-surface-sunken md:hidden"
      >
        {PHONE_BAR_DESTINATIONS.map((destination) => (
          <div key={destination.id} className="flex-1">
            <NavItem destination={destination} layout="bottom-bar" />
          </div>
        ))}
        <div className="flex-1">
          <button
            ref={moreButtonRef}
            type="button"
            onClick={() => setSheetOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            className="flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-md text-ink-secondary hover:bg-surface-hover"
          >
            <MenuIcon />
            <span className="text-body">Menu</span>
          </button>
        </div>
      </nav>
      <MoreSheet open={sheetOpen} onClose={closeSheet} triggerRef={moreButtonRef} />
    </>
  );
}
